// @tennis-matches/services/winterdoppel/draftAutofill

import { getSupabaseClient } from "../supabase/index.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __ts(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

function __maxTs(current, candidate) {
  const c = __ts(candidate);
  if (c == null) return current;
  if (current == null || c > current) return c;
  return current;
}

/**
 * Letzte planungsrelevante Änderung (Selbstpflege / Join / Blocks / Gruppen).
 *
 * @param {Array<object>} players
 * @param {Array<{ player_id?: string, created_by?: string|null, created_at?: string }>} blocks
 * @param {Array<object>} [playerGroups]
 * @returns {string|null} ISO-Zeitstempel
 */
export function latestWinterDoppelExternalPlanningChangeAt(
  players = [],
  blocks = [],
  playerGroups = [],
) {
  let latest = null;
  for (const p of players || []) {
    latest = __maxTs(latest, p?.self_service_at);
    if (!p?.created_by) {
      latest = __maxTs(latest, p?.created_at);
    }
  }
  for (const b of blocks || []) {
    if (!b?.created_by) {
      latest = __maxTs(latest, b?.created_at);
    }
  }
  for (const g of playerGroups || []) {
    latest = __maxTs(latest, g?.updated_at ?? g?.updatedAt);
    latest = __maxTs(latest, g?.created_at ?? g?.createdAt);
  }
  if (latest == null) return null;
  return new Date(latest).toISOString();
}

function hasStarterAssignments(sessionPlayers = []) {
  return (sessionPlayers || []).some(
    (sp) => sp?.role === "starter" && sp?.player_id,
  );
}

/**
 * @param {{
 *   series?: { draft_autofill_at?: string|null }|null,
 *   players?: object[],
 *   blocks?: object[],
 *   playerGroups?: object[],
 *   sessionPlayers?: object[],
 * }} input
 * @returns {{
 *   enabled: boolean,
 *   reason: "needPlayers"|"never"|"upToDate"|"stale"|"empty",
 *   draftAutofillAt: string|null,
 *   externalChangeAt: string|null,
 * }}
 */
export function resolveWinterDoppelAutofillButtonState(input = {}) {
  const players = input.players || [];
  const blocks = input.blocks || [];
  const playerGroups = input.playerGroups || [];
  const sessionPlayers = input.sessionPlayers || [];
  const draftAutofillAt = input.series?.draft_autofill_at
    ? String(input.series.draft_autofill_at)
    : null;

  if (!players.length) {
    return {
      enabled: false,
      reason: "needPlayers",
      draftAutofillAt,
      externalChangeAt: null,
    };
  }

  const externalChangeAt = latestWinterDoppelExternalPlanningChangeAt(
    players,
    blocks,
    playerGroups,
  );

  if (!draftAutofillAt) {
    return {
      enabled: true,
      reason: "never",
      draftAutofillAt: null,
      externalChangeAt,
    };
  }

  // Fehlgeschlagene Grobplanung (z. B. Constraint) hinterließ leeren Plan.
  if (!hasStarterAssignments(sessionPlayers)) {
    return {
      enabled: true,
      reason: "empty",
      draftAutofillAt,
      externalChangeAt,
    };
  }

  const autofillMs = __ts(draftAutofillAt);
  const externalMs = __ts(externalChangeAt);

  if (externalMs == null || autofillMs == null || externalMs <= autofillMs) {
    return {
      enabled: true,
      reason: "upToDate",
      draftAutofillAt,
      externalChangeAt,
    };
  }

  return {
    enabled: true,
    reason: "stale",
    draftAutofillAt,
    externalChangeAt,
  };
}

export async function markWinterDoppelSeriesDraftAutofill(seriesId) {
  const supabase = await getSupabaseClient();
  const now = new Date().toISOString();
  const { error } = await supabase
    .from("winterdoppel_series")
    .update({ draft_autofill_at: now })
    .eq("id", __asStr(seriesId));
  if (error) throw error;
  return now;
}
